import $ from 'jquery';
import 'datatables.net-fixedcolumns';
import iconVisibilityOff from '@disy/cadenza-icons/visibility-off.svg';
import isEqual from 'lodash-es/isEqual';
import last from 'lodash-es/last';

import type { DataTablesRequestParams } from 'ui/datatables/datatables';

import type { BaseTableHeaderContextMenuContentProvider, BaseTableRowElement } from 'cadenza/ui/base-table/base-table';
import { getColumnIndexForBackend, getColumnIndexForFrontend } from 'cadenza/ui/base-table/base-table';
import {
  COLUMN_WITH_HORIZONTAL_SORTING_BUTTON_IN_HEADER_CLASS,
  createHeaders,
  getTdThColumnTypeClass,
  getTdThOlapAttributeTypeClass,
  getTrRowTypeClass,
  LAST_PIVOT_ROW_COL_CLASS,
  PIVOT_LAST_CELL_IN_GROUP_CLASS,
  PIVOT_ROW_COL_CLASS,
  prepareHierarchicalColumns,
  reachedColumnCountLimit
} from 'cadenza/result-table/pivot-table-features/pivot-table-utils';
import { ResultTable, type ResultTableOptions } from 'cadenza/result-table/result-table';
import { getHeaderRowHasSubTitle } from 'cadenza/result-table/result-table-utils';
import { icon } from 'cadenza/utils/icon/icon';
import { PivotTableFeatures } from 'cadenza/result-table/pivot-table-features/pivot-table-features';
import { COLUMN_VISIBILITY_CHANGE } from 'cadenza/result-table/toolbar/table-settings/event-types';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { AbortError } from 'cadenza/api-client/abort-controller/abort-error';
import { logger } from 'cadenza/utils/logging';
import { setParam } from 'cadenza/utils/url-param-utils';
import type { DataTableSortEventDetails } from 'cadenza/result-table/pivot-table-features/event-types';
import { SORT_CHANGE } from 'cadenza/result-table/pivot-table-features/event-types';
import {
  FrozenColumnsOlapPivotTablePlugin
} from 'cadenza/result-table/frozen-columns/frozen-columns-olap-pivot-table-plugin';
import { RowSpanOlapPivotTablePlugin } from 'cadenza/result-table/row-span/row-span-olap-pivot-table-plugin';
import { ColSpanOlapPivotTablePlugin } from 'cadenza/result-table/col-span-olap-pivot-table-plugin';
import { assert } from 'cadenza/utils/custom-error';
import {
  DATATABLE_ROW_RENDERED,
  type DataTableRowRenderedEventDetail
} from 'cadenza/ui/base-table/base-table-event-types';
import type { ResponseProblem } from 'cadenza/api-client/api';
import type { TableColumnDto } from 'cadenza/ui/base-table/model/table-column-dto';
import type { ConditionChangeEventDetails } from 'cadenza/conditions/filter-controller';
import type { SortOrder } from 'cadenza/ui/base-table/sort-order';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';

import './olap-pivot-table.css'; // Load after result table to be able to overwrite base styles
import i18n from './olap-pivot-table_messages.properties';

const COMPONENT_NAME = 'd-olap-pivot-table';

const IS_PIVOT_CLASS = 'is-pivot';
const EXCEEDS_COLUMN_COUNT_LIMIT = COMPONENT_NAME + '--exceeds-column-limit';

export interface OlapPivotTableOptions extends ResultTableOptions {
  showColumnHideButton: boolean;
}

export class OlapPivotTable extends ResultTable {

  readonly #showColumnHideButton;  // If true, add a button allowing to hide a column
  #earlyAborted = false; // to indicate that data loading was aborted before the request started

  readonly #pivotTableFeatures;
  #olapPivotEvents?: EventKey[];

  constructor ({
    showColumnHideButton = true,
    ...options
  } = {}) {
    super(options);
    this.#showColumnHideButton = showColumnHideButton;

    this.#pivotTableFeatures = new PivotTableFeatures(this);
    this.append(this.#pivotTableFeatures);

    this.append(
      new FrozenColumnsOlapPivotTablePlugin(),
      new RowSpanOlapPivotTablePlugin(),
      new ColSpanOlapPivotTablePlugin());

    on<CustomEvent<DataTableRowRenderedEventDetail>>(
      this,
      DATATABLE_ROW_RENDERED,
      ({ detail: { row } }) => this.#afterTableRowRendered(row as BaseTableRowElement));
  }

  // Replace with readonly variable when converting to TS (IntelliJ might not see its actual usage)
  get pivotTableFeatures () {
    return this.#pivotTableFeatures;
  }

  get showColumnHideButton () {
    return this.#showColumnHideButton;
  }

  override connectedCallback () {
    this.classList.add(COMPONENT_NAME);

    this.#olapPivotEvents = [];

    if (this.#showColumnHideButton) {
      this.addHeaderContextMenuContentProvider(this.#createHeaderContextMenuContentProvider());
    }
    this.hideWhenTruncatedData = true;
    super.connectedCallback();
  }

  override initTableController () {
    super.initTableController();

    if (this.tableController) {
      this.tableController.setPivotTableFeatures(this.#pivotTableFeatures);
    }
  }

  override handleTableRefresh () {
    setParam({ paramName: 'forceRefresh', paramValue: true });
    // CADENZA-13788: changing data can change table configuration (for example number of pivot columns).
    // It's safe to always read config
    this.updateTableConfiguration();
  }

  override updateTableConfiguration (forceRedrawOfTable = false) {
    this.#enableEarlyAborting();
    this.cancelRequests(); // Cancel all currently running data or configuration requests
    return this.fetchConfigurationAndInitializeDataTable(forceRedrawOfTable);
  }

  /**
   * It may happen that the abort button that is visible during loading table configuration is created in table-settings
   * and does not cancel the data loading. Here we enable to "remember" that it was clicked and later we can cancel the request as soon as it starts.
   */
  #enableEarlyAborting () {
    const cancelButton = document.getElementById('cancel-result-table-load-button');
    if (cancelButton) {
      cancelButton.onclick = () => {
        this.#earlyAborted = true;
      };
    }
  }

  override disconnectedCallback () {
    super.disconnectedCallback();
    if (this.tableController) {
      this.tableController.resetResultTable();
      this.tableController.resetPivotTableFeatures();
      this.tableController = undefined;
    }
    unByKey(this.#olapPivotEvents);
  }

  get pivotConfiguration () {
    return this.tableConfiguration?.pivotConfiguration;
  }

  override hasPivotConfiguration () {
    return !!this.pivotConfiguration?.pivotTableConfig;
  }

  /**
   * Limit of the number of columns displayed in the table (excluding invisible columns and the column area hidden below
   * a cutting-off state indicating fading area on the right of the table.
   */
  get dataColumnCountLimit () {
    return this.hasPivotConfiguration()
      ? this.pivotConfiguration!.columnsNumberTableNotDisplayedThreshold
      : Number.POSITIVE_INFINITY;
  }

  override initializeDataTable () {
    this.classList.toggle(IS_PIVOT_CLASS, this.hasPivotConfiguration());
    const multilineHeaders = this.hasPivotConfiguration()
      ? createHeaders(
        getHeaderRowHasSubTitle(this.getColumns()),
        this.getHierarchicalColumns()!)
      : undefined;
    unByKey(this.#olapPivotEvents);
    this.#olapPivotEvents = [];
    super.initializeDataTable(multilineHeaders);
    this.dataTable!.on('order.dt', this.#onOrderChanged.bind(this));
  }

  override async fetchConfigurationAndInitializeDataTable (forceRedrawOfTable = false) {
    try {
      const tableConfiguration = await this.fetchConfiguration();
      if (forceRedrawOfTable || !isEqual(this.tableConfiguration, tableConfiguration)) {
        this.destroyDataTable();
        if (this.isInvalidTableConfiguration) {
          this.initializeDataTable();
        } else {
          this.tableConfiguration = tableConfiguration;
        }
        this.updateMessages();
      } else {
        await this.updateTableData();
      }
    } catch (error) {
      if (error instanceof AbortError) {
        // do nothing as the only way this gets aborted is, when
        // fetchConfigurationAndInitializeDataTable has been called again
      } else {
        this.handleLoadDataError(error as ResponseProblem);
        logger.error('Failed to load table configuration', error);
      }
      if (this.isInvalidTableConfiguration) {
        await this.initializeDataTable();
      }
    }
  }

  override shouldLoadData () {
    if (this.isInvalidTableConfiguration) {
      return false;
    }
    return super.shouldLoadData();
  }

  override onFirstTableDraw () {
    super.onFirstTableDraw();
    if (this.tableController) { // classic only
      this.tableController.setPivotTableFeatures(this.#pivotTableFeatures);
    }
  }

  #afterTableRowRendered (trElement: BaseTableRowElement) {
    this.#setRowTypeOfRow(trElement);
    this.#setOlapAttributeAndColumnTypesOnCells(trElement);
  }

  #setRowTypeOfRow (trElement: BaseTableRowElement) {
    trElement.classList.add(getTrRowTypeClass(trElement.dData.rowType));
  }

  #setOlapAttributeAndColumnTypesOnCells (trElement: HTMLTableRowElement) {
    for (const tdElement of trElement.children) {
      this.#setOlapAttributeAndColumnTypeClass(tdElement as HTMLTableCellElement);
    }
  }

  override getColumnDefinitions (columns: TableColumnDto[], targetIndex = 0) {
    const additionalColumnClasses: Record<number, string[]> = {};
    $(this.multilineHeaders!).children().last().children().each((index, th) => {
      additionalColumnClasses[index] = [];
      if (th.classList.contains(PIVOT_ROW_COL_CLASS)) {
        additionalColumnClasses[index].push(PIVOT_ROW_COL_CLASS);
      }
      if (th.classList.contains(LAST_PIVOT_ROW_COL_CLASS)) {
        additionalColumnClasses[index].push(LAST_PIVOT_ROW_COL_CLASS);
      }
      if (th.classList.contains(PIVOT_LAST_CELL_IN_GROUP_CLASS)) {
        additionalColumnClasses[index].push(PIVOT_LAST_CELL_IN_GROUP_CLASS);
      }
      if (th.classList.contains(COLUMN_WITH_HORIZONTAL_SORTING_BUTTON_IN_HEADER_CLASS)) {
        additionalColumnClasses[index].push(COLUMN_WITH_HORIZONTAL_SORTING_BUTTON_IN_HEADER_CLASS);
      }
    });
    const definitions = super.getColumnDefinitions(columns, targetIndex);
    definitions.forEach((def, index) => {
      const classNames = additionalColumnClasses[index];
      if (classNames) {
        classNames.forEach((className) => {
          def.className = `${def.className} ${className}`;
        });
      }
    });
    return definitions;
  }

  override shouldUpdateTableConfiguration (conditionsOrHierarchiesChangedEvent: CustomEvent<ConditionChangeEventDetails>) {
    return super.shouldUpdateTableConfiguration(conditionsOrHierarchiesChangedEvent) || this.hasPivotConfiguration();
  }

  override beforeLoadedData () {
    this.hideMessages();
    super.beforeLoadedData();
  }

  override prepareRequestParams (requestParams: DataTablesRequestParams) {
    const prepareRequestParams = super.prepareRequestParams(requestParams);
    if (this.#earlyAborted) {
      this.cancelDataLoading();
      this.#earlyAborted = false;
    }
    return prepareRequestParams;
  }

  #createHeaderContextMenuContentProvider (): BaseTableHeaderContextMenuContentProvider {
    return columnIndex => {
      if (this.hasPivotConfiguration()
        // statement uses 2 because of the numbering column which is always present
        // in non-workbook context. This prevents the last column from being hidden
        // which results in an illegal table configuration
        || this.dataTable!.columns().count() <= 2
        // Not for index column
        || columnIndex === 0) {
        return [];
      }

      const columnDto = this.getColumns()[getColumnIndexForBackend(columnIndex)];
      return [
        {
          icon: icon(iconVisibilityOff),
          label: i18n(columnDto.hierarchyName ? 'hideHierarchy' : 'hideColumn'),
          execute: () => {
            const columnClicked = columnDto;
            const tableColumnNamesWithVisibleIndicator: { name: string; visible: boolean }[] = [];
            this.configurationColumns.forEach(col => {
              const name = col.hierarchyName || col.name;
              // push column if not another column of same hierarchy exists
              if (!tableColumnNamesWithVisibleIndicator.find(c => c.name === name)) {
                tableColumnNamesWithVisibleIndicator.push({
                  name,
                  visible: (col.name === columnClicked.name || (col.hierarchyName && col.hierarchyName === columnClicked.hierarchyName)) ? false : col.visible
                });
              }
            });
            this.dispatchEvent(
              new CustomEvent(COLUMN_VISIBILITY_CHANGE,
                {
                  detail: { tableColumnNamesWithVisibleIndicator },
                  bubbles: true
                })
            );
          }
        }
      ];
    };
  }

  #onOrderChanged () {
    this.#raiseSortChangeEvent();
  }

  #getHierarchyNameFromColumnName (columnName: string) {
    return this.getColumns().find(c => c.name === columnName)?.hierarchyName;
  }

  override setColumnOrder (columnIdx: number, order: SortOrder) {
    if (this.isSortOrderEstablishedByBackend) {
      assert(this.dataTable, 'Datatable not yet initialized');

      // This route short-circuits the datatable's sorting code. The event
      // will be processed through the registered sortListener
      const columnName = this.getColumnNameForDatatableIndex(columnIdx);
      const columnHierarchyName = this.#getHierarchyNameFromColumnName(columnName);
      this.sortListener!(this.dataTable, { columnName, columnHierarchyName, order });
      return;
    }

    super.setColumnOrder(columnIdx, order);
  }

  /**
   * Raises a SORT_CHANGE custom event when the order changes
   * The detail of the event is of type {DataTableSortEventDetails}
   */
  #raiseSortChangeEvent () {
    if (!this.sortListener) {
      const order = this.order;
      if (order) {
        const columnHierarchyName = this.#getHierarchyNameFromColumnName(order.columnName);
        const detail = { columnHierarchyName, ...order };
        this.dispatchEvent(new CustomEvent<DataTableSortEventDetails>(SORT_CHANGE, { detail }));
      }
    }
  }

  /**
   * Prepare columns according to config.
   *
   * @protected
   * @param columnsData - columns configuration
   * @return structure representing the columns and the hierarchicalColumns if any
   */
  override prepareColumns (columnsData: TableColumnDto[]) {
    const hierarchicalColumns = prepareHierarchicalColumns(
      columnsData,
      column => this.prepareColumn(column),
      this.dataColumnCountLimit + 1); // Render one more to have one that fades out (partly visible)
    const { multiColumns, rowColumns = [] } = hierarchicalColumns;
    return {
      columns: [ ...rowColumns, ...(last(multiColumns) ?? []) ],
      hierarchicalColumns };
  }

  override configureWithConfigurationData (tableConfiguration: TableConfigurationDto) {
    super.configureWithConfigurationData(tableConfiguration);

    this.classList.toggle(
      EXCEEDS_COLUMN_COUNT_LIMIT,
      reachedColumnCountLimit(this.getHierarchicalColumns()!, this.dataColumnCountLimit));
  }

  override fillFooterCell (columnIndex: number, title: string, value: string) {
    const footer = super.fillFooterCell(columnIndex, title, value);
    if (footer) {
      this.#setOlapAttributeAndColumnTypeClass(footer);
    }
    return footer;
  }

  #setOlapAttributeAndColumnTypeClass (tdOrThElement: HTMLTableCellElement) {
    const columnIndex = this.getColumnIndex(tdOrThElement);
    const columnType = this.#getColumnTypeByColumnIndex(columnIndex);
    const olapAttributeType = this.#getOlapAttributeTypeByColumnIndex(columnIndex);

    if (columnType) { // null for index column
      tdOrThElement.classList.add(getTdThColumnTypeClass(columnType));
    }
    if (olapAttributeType) { // null for index column
      tdOrThElement.classList.add(getTdThOlapAttributeTypeClass(olapAttributeType));
    }
  }

  /**
   * Get ColumnType by column index in column definition space (including index and invisible columns)
   *
   * @param columnIndex - Index of the column in column definition space to get the ColumnType of
   * @return The ColumnType of the given index. Null for the index column
   */
  #getColumnTypeByColumnIndex (columnIndex: number) {
    const column = this.getColumns()[getColumnIndexForBackend(columnIndex)];
    if (column) {
      return column.columnType;
    }
    return null; // For index column
  }

  /**
   * Get ColumnType by column index in column definition space (including index and invisible columns)
   *
   * @param columnIndex - Index of the cell in column definition space to get the ColumnType of
   * @return The OlapAttributeDefinitionType of the given index.
   *   Null for the index column
   */
  #getOlapAttributeTypeByColumnIndex (columnIndex: number) {
    const column = this.getColumnByColumnIndex(columnIndex);
    if (column) {
      return column.olapAttributeDefinitionType;
    }
    return null; // For index column
  }

  /**
   * Returns all column indices in column definition space (including index and invisible columns)
   * of columns containing measure values.
   *
   * @return All measure column's indices in column definition space
   */
  getMeasureColumnIndexes () {
    return this.getColumns()
      .flatMap(({ olapAttributeDefinitionType }, dataColumnIndex) =>
        // Pivot table measures use NONE
        (olapAttributeDefinitionType !== 'DIMENSION')
          ? getColumnIndexForFrontend(dataColumnIndex)
          : []);
  }

  /**
   * Returns the column identified by the given column id.
   *
   * @param columnId - Column id to return the corresponding column configuration for
   * @return The identified column or null if no column with the given id was found
   */
  getColumnByColumnId (columnId: string) {
    return this.getColumns().find(column => column.columnId === columnId);
  }

  /**
   * Returns the column index in column definition space (including index and invisible columns).
   *
   * @param columnId - Column id to return the corresponding index for
   * @return The index of the column or null if no column was found with the given columnId
   */
  getColumnIndexByColumnId (columnId: string) {
    const columnIndex = this.getColumns().findIndex(column => column.columnId === columnId);
    if (columnIndex === -1) {
      return -1;
    }
    return getColumnIndexForFrontend(columnIndex);
  }

  /**
   * Returns the column identified by its column definition index (excluding invisible columns)
   *
   * @param columnIndex - Column index to return the corresponding column configuration for
   * @return The identified column or null in case of index column
   */
  getColumnByColumnIndex (columnIndex: number) {
    return this.getColumns()[getColumnIndexForBackend(columnIndex)];
  }

  get dataColumnCountLimitExceeded () {
    return this.classList.contains(EXCEEDS_COLUMN_COUNT_LIMIT); // Use this cached result to not calculate again
  }

}

customElements.define(COMPONENT_NAME, OlapPivotTable);
